import {onMounted, onUnmounted, Ref} from 'vue';
import CommonConstants from '@/utils/constants/CommonConstants.ts';

const ARABIC_KEYBOARD = CommonConstants.ARABIC_KEYBOARD_;
const ENGLISH_KEYBOARD = CommonConstants.ENGLISH_KEYBOARD;

const LOWER_CASE = 'LowerCase';
const UPPER_CASE = 'UpperCase';

/**
 * Maps physical key codes (KeyboardEvent.code) to indices in the ARABIC_KEYBOARD / ENGLISH_KEYBOARD arrays.
 * Using e.code instead of e.key lets us identify the physical key regardless of the active keyboard layout,
 * which is essential for correct Shift / CapsLock handling (e.g. Shift on Arabic layout produces diacritics
 * that don't appear in the mapping arrays at all).
 */
const CODE_TO_INDEX: Record<string, number> = {
  'KeyA': 0, 'KeyB': 1, 'KeyC': 2, 'KeyD': 3, 'KeyE': 4, 'KeyF': 5,
  'KeyG': 6, 'KeyH': 7, 'KeyI': 8, 'KeyJ': 9, 'KeyK': 10, 'KeyL': 11,
  'KeyM': 12, 'KeyN': 13, 'KeyO': 14, 'KeyP': 15, 'KeyQ': 16, 'KeyR': 17,
  'KeyS': 18, 'KeyT': 19, 'KeyU': 20, 'KeyV': 21, 'KeyW': 22, 'KeyX': 23,
  'KeyY': 24, 'KeyZ': 25,
  'BracketRight': 26, 'BracketLeft': 27, 'Quote': 28, 'Semicolon': 29,
  'Slash': 30, 'Period': 31, 'Comma': 32, 'Backquote': 33
};

const SHIFTED_SYMBOLS: Record<string, string> = {
  ']': '}', '[': '{', "'": '"', ';': ':', '/': '?', '.': '>', ',': '<', '`': '~'
};

function isAsciiPrintable(key: string): boolean {
  if (key.length !== 1) return false;
  const code = key.charCodeAt(0);
  return code >= 0x20 && code <= 0x7E;
}

function insertTextAtCursor(element: HTMLInputElement | HTMLTextAreaElement, text: string) {
  const start = element.selectionStart ?? element.value.length;
  const end = element.selectionEnd ?? element.value.length;
  const newValue = element.value.substring(0, start) + text + element.value.substring(end);
  const proto = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (nativeSetter) {
    nativeSetter.call(element, newValue);
    element.selectionStart = element.selectionEnd = start + text.length;
    element.dispatchEvent(new InputEvent('input', {bubbles: true, data: text, inputType: 'insertText'}));
  }
}

function createHandler(fixedInputLanguage: string | undefined, fixedLetterCase: string | undefined) {
  let blockNextBeforeInput = false;

  function setBlockNextBeforeInput() {
    blockNextBeforeInput = true;
    setTimeout(() => { blockNextBeforeInput = false; }, 0);
  }

  function onBeforeInput(e: InputEvent) {
    if (blockNextBeforeInput && e.inputType === 'insertText') {
      e.preventDefault();
      blockNextBeforeInput = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement))
      return;
    if (target.readOnly || target.disabled)
      return;
    if (e.ctrlKey || e.altKey || e.metaKey)
      return;

    const index = CODE_TO_INDEX[e.code];
    if (index === undefined)
      return;

    const isLetterKey = index < 26;

    if (fixedInputLanguage === 'English') {
      if (isAsciiPrintable(e.key))
        return;
      e.preventDefault();
      setBlockNextBeforeInput();
      let charToInsert = ENGLISH_KEYBOARD[index];
      if (isLetterKey) {
        const shouldBeUpperCase = e.shiftKey !== e.getModifierState('CapsLock');
        if (shouldBeUpperCase)
          charToInsert = charToInsert.toUpperCase();
        if (fixedLetterCase === UPPER_CASE)
          charToInsert = charToInsert.toUpperCase();
        else if (fixedLetterCase === LOWER_CASE)
          charToInsert = charToInsert.toLowerCase();
      } else if (e.shiftKey && SHIFTED_SYMBOLS[charToInsert]) {
        charToInsert = SHIFTED_SYMBOLS[charToInsert];
      }
      insertTextAtCursor(target as HTMLInputElement | HTMLTextAreaElement, charToInsert);

    } else if (fixedInputLanguage === 'Arabic') {
      if (isLetterKey) {
        if (!/^[a-zA-Z]$/.test(e.key))
          return;
      } else {
        if (e.key !== ENGLISH_KEYBOARD[index])
          return;
      }
      e.preventDefault();
      setBlockNextBeforeInput();
      insertTextAtCursor(target as HTMLInputElement | HTMLTextAreaElement, ARABIC_KEYBOARD[index]);
    }
  }

  return {handleKeydown, onBeforeInput};
}

export function attachInputRestrictionsHandler(
  element: HTMLElement,
  fixedInputLanguage: string | undefined,
  fixedLetterCase: string | undefined
): () => void {
  const {handleKeydown, onBeforeInput} = createHandler(fixedInputLanguage, fixedLetterCase);
  element.addEventListener('keydown', handleKeydown, true);
  element.addEventListener('beforeinput', onBeforeInput as EventListener, true);
  return () => {
    element.removeEventListener('keydown', handleKeydown, true);
    element.removeEventListener('beforeinput', onBeforeInput as EventListener, true);
  };
}

export function useInputRestrictions(
  containerRef: Ref<HTMLElement | { $el: HTMLElement } | undefined | null>,
  fixedInputLanguage: string | undefined,
  fixedLetterCase: string | undefined
) {
  if (!fixedInputLanguage)
    return;

  let cleanup: (() => void) | null = null;

  onMounted(() => {
    const raw = containerRef.value;
    const el = raw instanceof HTMLElement ? raw : (raw as any)?.$el as HTMLElement | undefined;
    if (el)
      cleanup = attachInputRestrictionsHandler(el, fixedInputLanguage, fixedLetterCase);
  });

  onUnmounted(() => {
    cleanup?.();
  });
}
